import React, { useEffect, useRef, useState } from "react";
import TrashIcon from "assets/icons/HeroIcons/TrashIcon";
import QuestionPrompt from "../QuestionPrompt";
import { TextField } from "../TextField";
import MatrixDropdown from "./MatrixDropdown";

interface MatrixDynamicProps {
  element: any;
  name: string;
  value?: any[];
  editMode?: boolean;
  error?: any;
  touched?: any;
  handleChange: (rows: any[]) => void;
  commentValue?: string;
  handleCommentChange?: (comment: string) => void;
}

const getPositiveInteger = (value: any, fallback: number) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0
    ? Math.floor(parsedValue)
    : fallback;
};

const normalizeColumnName = (name: any) =>
  String(name ?? "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

const getInitialRowCount = (
  rowCount: number,
  minRowCount: number,
  maxRowCount: number,
) => {
  if (rowCount > maxRowCount) return maxRowCount;
  if (rowCount < minRowCount) return minRowCount;
  return rowCount;
};

export const MatrixDynamic: React.FC<MatrixDynamicProps> = ({
  element,
  name,
  value,
  editMode = true,
  error,
  touched,
  handleChange,
  commentValue,
  handleCommentChange,
}) => {
  const configuredColumns = Array.isArray(element?.columns)
    ? element.columns
    : [];
  const columns = configuredColumns.filter(
    (column: any) => column.visible !== false,
  );
  const minRowCount = getPositiveInteger(element?.minRowCount, 0);
  const configuredMaxRowCount = getPositiveInteger(
    element?.maxRowCount,
    Number.MAX_SAFE_INTEGER,
  );
  const maxRowCount = Math.max(minRowCount, configuredMaxRowCount);
  const configuredRowCount = getPositiveInteger(element?.rowCount, minRowCount);
  const initialRowCount = getInitialRowCount(
    configuredRowCount,
    minRowCount,
    maxRowCount,
  );

  const createEmptyRow = () => {
    const row =
      element?.defaultRowValue && typeof element.defaultRowValue === "object"
        ? { ...element.defaultRowValue }
        : {};

    configuredColumns.forEach((column: any) => {
      if (column.defaultValue !== undefined && row[column.name] === undefined) {
        row[column.name] = column.defaultValue;
      }
    });

    return row;
  };

  const mapAnswerRowToColumns = (answerRow: any) => {
    if (!answerRow || typeof answerRow !== "object") return {};

    const row: Record<string, any> = {};
    const answerKeys = Object.keys(answerRow);

    configuredColumns.forEach((column: any) => {
      const normalizedColumnName = normalizeColumnName(column.name);
      const matchingKeys = answerKeys.filter(
        (key) => normalizeColumnName(key) === normalizedColumnName,
      );
      const matchingKey =
        matchingKeys.find((key) => key === column.name) ?? matchingKeys[0];

      if (matchingKey !== undefined) {
        row[column.name] = answerRow[matchingKey];
      }
    });

    return row;
  };

  const createInitialRows = (answer: any, includeConfiguredCount: boolean) => {
    const answerRows = Array.isArray(answer)
      ? answer.slice(0, maxRowCount).map(mapAnswerRowToColumns)
      : [];
    const requiredRowCount = includeConfiguredCount
      ? initialRowCount
      : minRowCount;
    const rowTotal = Math.min(
      maxRowCount,
      Math.max(requiredRowCount, answerRows.length),
    );

    while (answerRows.length < rowTotal) {
      answerRows.push(createEmptyRow());
    }

    return answerRows;
  };

  const [rows, setRows] = useState<any[]>(() => createInitialRows(value, true));
  const latestLocalValue = useRef<any>(value);

  const mapRowsToAnswer = (nextRows: any[]) =>
    nextRows.map((nextRow) => ({ ...nextRow }));

  useEffect(() => {
    if (value === latestLocalValue.current) return;
    latestLocalValue.current = value;
    setRows(createInitialRows(value, false));
  }, [value]);

  const updateRows = (nextRows: any[]) => {
    const answerRows = mapRowsToAnswer(nextRows);
    latestLocalValue.current = answerRows;
    setRows(nextRows);
    handleChange(answerRows);
  };

  useEffect(() => {
    const constrainedRows = rows.slice(0, maxRowCount);

    while (constrainedRows.length < minRowCount) {
      constrainedRows.push(createEmptyRow());
    }

    if (constrainedRows.length !== rows.length) {
      updateRows(constrainedRows);
    }
  }, [minRowCount, maxRowCount]);

  const updateCell = (rowIndex: number, columnName: string, nextValue: any) => {
    updateRows(
      rows.map((row, index) =>
        index === rowIndex ? { ...row, [columnName]: nextValue } : row,
      ),
    );
  };

  const addRow = () => {
    if (rows.length >= maxRowCount) return;
    const nextRow = createEmptyRow();
    updateRows(
      element?.newRowPosition === "top"
        ? [nextRow, ...rows]
        : [...rows, nextRow],
    );
  };

  const removeRow = (rowIndex: number) => {
    if (rows.length <= minRowCount) return;
    if (
      element?.confirmDelete &&
      !window.confirm(
        element.confirmDeleteText || "Do you want to remove this row?",
      )
    ) {
      return;
    }
    updateRows(rows.filter((_, index) => index !== rowIndex));
  };

  const renderCell = (column: any, row: any, rowIndex: number) => {
    const cellType =
      !column.cellType || column.cellType === "default"
        ? element?.cellType || "dropdown"
        : column.cellType;
    const cellValue = row?.[column.name];
    const isDisabled =
      !editMode ||
      element?.readOnly === true ||
      column.readOnly === true ||
      column.enable === false;

    const cellName = `${name}.${rowIndex}.${column.name}`;
    const hideLabel = element?.showHeader !== false;

    if (cellType === "dropdown") {
      const columnChoices = Array.isArray(column.choices)
        ? column.choices
        : Array.isArray(element?.choices)
          ? element.choices
          : [];
      const columnChoicesByUrl =
        column.choicesByUrl ?? element?.choicesByUrl ?? null;

      return (
        <MatrixDropdown
          choices={columnChoices}
          choicesByUrl={columnChoicesByUrl}
          disabled={isDisabled}
          editMode={editMode && !isDisabled}
          handleChange={(selectedChoice: any) =>
            updateCell(rowIndex, column.name, selectedChoice)
          }
          label={column.title || column.name}
          name={cellName}
          placeholder={column.placeholder || "Select"}
          required={column.isRequired === true}
          showLabel={!hideLabel}
          value={cellValue}
        />
      );
    }

    return (
      <TextField
        disabled={isDisabled}
        editMode={editMode && !isDisabled}
        element={{
          ...column,
          questionId: `${element?.questionId ?? name}-${rowIndex}-${column.name}`,
          type: cellType,
        }}
        fieldAs={column.fieldAs}
        handleChange={(nextValue: any) =>
          updateCell(rowIndex, column.name, nextValue)
        }
        hideLabel={hideLabel}
        inputClassName={column.inputClassName}
        label={column.title || column.name}
        labelSize="xs"
        max={column.max}
        maxLength={column.maxLength}
        min={column.min}
        name={cellName}
        placeholder={column.placeholder || "Type"}
        readOnly={column.readOnly === true}
        required={column.isRequired === true}
        step={column.step}
        type={column.inputType || "text"}
        value={cellValue ?? ""}
      />
    );
  };

  const canAddRows =
    editMode &&
    element?.readOnly !== true &&
    element?.allowAddRows !== false &&
    rows.length < maxRowCount;
  const canRemoveRows =
    editMode &&
    element?.readOnly !== true &&
    element?.allowRemoveRows !== false &&
    rows.length > minRowCount;
  const matrixGridColumns = `${columns
    .map(() => "minmax(240px, 1fr)")
    .join(" ")}${canRemoveRows ? " 56px" : ""}`;
  const hasColumns = columns.length > 0;

  return (
    <div
      className="flex flex-col w-full gap-3 p-4 rounded-lg bg-bgPrimaryLight dark:bg-bgtetriary"
      id={`${element?.questionId ?? name}`}
    >
      <QuestionPrompt
        label={element?.title || name}
        name={name}
        description={element?.description}
        required={element?.isRequired}
        editMode={editMode}
      />

      {hasColumns ? (
        <div className="w-full overflow-x-auto">
          <div className="w-full min-w-max">
            {element?.showHeader !== false && columns.length > 0 && (
              <div
                className="grid items-stretch"
                style={{ gridTemplateColumns: matrixGridColumns }}
              >
                {columns.map((column: any) => (
                  <div
                    className="flex min-h-14 items-center justify-center px-4 py-3 text-sm font-semibold text-center bg-bgBluish_2 text-textNormal dark:bg-secondaryMid dark:text-textMain"
                    key={column.name}
                  >
                    {column.title || column.name}
                    {column.isRequired ? " *" : ""}
                  </div>
                ))}
                {canRemoveRows && <div aria-hidden="true" />}
              </div>
            )}

            <div className="flex flex-col gap-1 pt-2">
              {rows.map((row, rowIndex) => (
                <div
                  className="grid items-center gap-x-4"
                  key={rowIndex}
                  style={{ gridTemplateColumns: matrixGridColumns }}
                >
                  {columns.map((column: any) => (
                    <div className="min-w-0" key={column.name}>
                      {renderCell(column, row, rowIndex)}
                    </div>
                  ))}
                  {canRemoveRows && (
                    <button
                      aria-label={element?.removeRowText || "Remove row"}
                      className="flex h-12 w-12 items-center justify-center rounded opacity-50 text-secondaryLight transition-colors hover:bg-primary_5 hover:text-primary hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary_40"
                      type="button"
                      onClick={() => removeRow(rowIndex)}
                    >
                      <TrashIcon fill="currentColor" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {rows.length === 0 && (
            <p className="py-3 italic text-textNoSelection">
              {element?.noRowsText || "There are no rows."}
            </p>
          )}
        </div>
      ) : (
        <p className="py-3 italic text-textNoSelection">
          No matrix columns are configured.
        </p>
      )}

      {hasColumns && canAddRows && (
        <button
          className="self-start px-0 py-2 text-base font-semibold text-secondaryLight transition-colors hover:text-primary focus:outline-none focus:underline"
          type="button"
          onClick={addRow}
        >
          {element?.addRowText || "Add row"}
        </button>
      )}

      {element?.showCommentArea === true && (
        <TextField
          disabled={element?.readOnly === true}
          editMode={editMode && element?.readOnly !== true}
          element={{ ...element, type: "comment" }}
          fieldAs="textarea"
          handleChange={(comment: string) => handleCommentChange?.(comment)}
          label={element?.commentText || "Other (describe)"}
          labelSize="sm"
          name={`${name}-Comment`}
          placeholder={element?.commentPlaceholder || "Type a comment"}
          rows={3}
          type="text"
          value={commentValue ?? ""}
        />
      )}

      {touched && error && (
        <p className="text-xs text-accent_1Dark">{String(error)}</p>
      )}
    </div>
  );
};

export default MatrixDynamic;
